// Renders a single question + its options.
//
// The wizard cycles through 43 questions across four very different
// shapes:
//
//   * RTQ (10)         alphabetic A-E options, each with a rich label
//                      e.g. { A: "Sell most of my investments", ... }
//   * Behavioral (20)  numeric "1".."5" (or "1".."3"/"1".."4") options
//                      with rich labels
//   * Financial (5)    same shape as Behavioral
//   * Style (8)        bare 1-5 likert (Strongly disagree ... Strongly
//                      agree) -- no per-option labels, just the prompt.
//
// To avoid sprouting four near-identical components, we accept a
// normalised ``options`` array and keep the render path uniform.
//
// Visual language matches the rest of the platform: rounded-2xl
// surface, slate base, green accent on the active option, focus-visible
// ring-green-400, dark-mode parity.

import { useEffect, useRef } from 'react';

export interface QuestionOption {
    /** Wire value sent back to the API (e.g. "A" or 3). */
    value: string | number;
    /** Human-readable label rendered next to the radio. */
    label: string;
}

export interface QuestionCardProps {
    /** Position of this question within its section, e.g. "Q3 of 20". */
    indexLabel?: string;
    /** Section name surfaced above the prompt for context. */
    sectionLabel?: string;
    /** Optional behavioral-bucket tag (e.g. "Emotional / Psychological"). */
    categoryLabel?: string;
    /** The actual prompt the user has to answer. */
    prompt: string;
    /** Normalised options the user can pick from. */
    options: QuestionOption[];
    /** Currently-selected value, or undefined for an unanswered question. */
    value: string | number | undefined;
    /** Fired when the user picks an option. */
    onChange: (value: string | number) => void;
    /**
     * When true, surrounds the prompt with a red ring to flag a
     * missing answer after the user clicks Calculate. The wizard
     * sets this in response to a 422 from /calculate-full-profile.
     */
    invalid?: boolean;
}

const RadioDot = ({ checked }: { checked: boolean }) => (
    <span
        aria-hidden
        className={[
            'mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all',
            checked
                ? 'border-green-500 bg-green-500 dark:border-emerald-400 dark:bg-emerald-400'
                : 'border-slate-300 dark:border-slate-600 group-hover:border-green-400 dark:group-hover:border-emerald-400',
        ].join(' ')}
    >
        {checked && <span className="h-2 w-2 rounded-full bg-white" />}
    </span>
);

export const QuestionCard = ({
    indexLabel,
    sectionLabel,
    categoryLabel,
    prompt,
    options,
    value,
    onChange,
    invalid,
}: QuestionCardProps) => {
    // Auto-scroll the card into view when it mounts so the user always
    // sees the active question at the top of the viewport instead of
    // having to scroll past stale answered questions on small screens.
    const rootRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        rootRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, [prompt]);

    return (
        <div
            ref={rootRef}
            // The animation key is the prompt itself so React re-mounts
            // the node when the wizard advances, replaying the
            // fade-slide-up keyframe from theme.css.
            key={prompt}
            className={[
                'surface-card animate-fade-slide-up',
                invalid
                    ? 'border-red-400 dark:border-red-500 ring-2 ring-red-300/60 dark:ring-red-700/60'
                    : '',
            ].join(' ')}
        >
            {/* Pre-prompt context strip. Renders a section + index + an
                optional bucket pill so the user always knows where they
                are in the questionnaire. */}
            {(sectionLabel || indexLabel || categoryLabel) && (
                <div className="mb-3 flex flex-wrap items-center gap-2 text-xs uppercase tracking-wide muted">
                    {sectionLabel && <span>{sectionLabel}</span>}
                    {sectionLabel && indexLabel && <span aria-hidden>•</span>}
                    {indexLabel && <span>{indexLabel}</span>}
                    {categoryLabel && (
                        <span className="ml-auto rounded-full border border-slate-300 dark:border-slate-700 px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal">
                            {categoryLabel}
                        </span>
                    )}
                </div>
            )}

            <h3 className="mb-6 text-lg sm:text-xl font-semibold text-slate-900 dark:text-white">
                {prompt}
            </h3>

            <div className="space-y-3">
                {options.map((opt) => {
                    const isActive = value === opt.value;
                    return (
                        <button
                            key={String(opt.value)}
                            type="button"
                            onClick={() => onChange(opt.value)}
                            aria-pressed={isActive}
                            className={[
                                'group w-full rounded-xl border-2 p-4 text-left transition-all',
                                'focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400',
                                isActive
                                    ? 'border-green-500 bg-green-50 dark:border-emerald-400 dark:bg-emerald-500/10'
                                    : 'border-slate-200 dark:border-slate-700 hover:border-green-400 dark:hover:border-emerald-400 hover:bg-slate-50 dark:hover:bg-slate-800/40',
                            ].join(' ')}
                        >
                            <div className="flex items-start gap-3">
                                <RadioDot checked={isActive} />
                                <span
                                    className={[
                                        'text-sm sm:text-base',
                                        isActive
                                            ? 'text-slate-900 dark:text-white font-medium'
                                            : 'text-slate-700 dark:text-slate-300',
                                    ].join(' ')}
                                >
                                    {opt.label}
                                </span>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default QuestionCard;
